/**
 * Support / ticketing API — real backend (via axios).
 *
 * CUSTOMER ↔ PLATFORM SUPPORT tickets. As of backend v6 the ticketing system
 * was split into three domains; this file covers the customer's own
 * *support* tickets (user ↔ support agent). The customer-facing routes moved
 * under `/user/support-tickets`:
 *
 *   GET    /user/support-tickets              → list my support tickets
 *   POST   /user/support-tickets              → open a ticket to support
 *   GET    /user/support-tickets/:id          → one of my tickets + thread
 *   POST   /user/support-tickets/:id/messages → my reply (author 'user')
 *
 * The agent side (GET/POST/PATCH /support/customer-tickets) is handled by the
 * support console, not this PWA.
 *
 * A SECOND customer ticket domain also exists in v6 — clinic tickets
 * (user ↔ clinic) under `/user/clinic-tickets`. That is a separate feature
 * with no UI in this app yet; it is intentionally NOT wired here.
 *
 * All requests go through the shared axios instance, which:
 *   - attaches the Bearer access token
 *   - refreshes on 401 transparently
 *   - shapes errors as err.response.data.error.{code,message,messageEn,details}
 *
 * SHAPE NORMALIZATION (see normalizeTicket below): the backend's
 * `serialize_ticket_message` emits each message timestamp as `at`, while the
 * rest of the app (components + the mock) read `createdAt`. We remap it here
 * at the boundary so callers see one consistent shape regardless of whether
 * mocks or the real backend are active. Ticket-level fields already match
 * (relatedType / relatedId / createdAt / updatedAt / messageCount). The
 * backend also adds harmless extras (domain / userPhone / userName) that the
 * UI ignores.
 */

import axiosInstance from './axiosInstance';

/**
 * Normalize one backend message to the frontend shape.
 * Backend key is `at` (serialize_ticket_message); the app reads `createdAt`.
 * @param {{ id: string, author: string, body: string, at?: string, createdAt?: string }} msg
 * @returns {{ id: string, author: string, body: string, createdAt: string }}
 */
function normalizeMessage(msg) {
  return {
    id: msg.id,
    author: msg.author,
    body: msg.body,
    createdAt: msg.createdAt ?? msg.at,
  };
}

/**
 * Normalize a backend ticket so its messages carry `createdAt`.
 * Leaves ticket-level fields untouched (they already match the frontend shape).
 * Safe on list rows too, where `messages` is an empty array.
 * @param {object|undefined} ticket raw ticket from the backend
 * @returns {object|undefined} ticket with normalized messages
 */
function normalizeTicket(ticket) {
  if (!ticket) return ticket;
  if (!Array.isArray(ticket.messages)) return ticket;
  return { ...ticket, messages: ticket.messages.map(normalizeMessage) };
}

export async function listTickets() {
  const res = await axiosInstance.get('/user/support-tickets');
  const body = res.data;
  return {
    ...body,
    data: {
      ...body.data,
      tickets: (body.data?.tickets ?? []).map(normalizeTicket),
    },
  };
}

export async function getTicket(id) {
  const res = await axiosInstance.get(`/user/support-tickets/${id}`);
  const body = res.data;
  return { ...body, data: { ...body.data, ticket: normalizeTicket(body.data?.ticket) } };
}

/**
 * @param {{
 *   subject: string,
 *   category: 'general'|'service_complaint'|'payment_issue'|'technical'|'account'|'other',
 *   message: string,
 *   relatedType?: 'service_history'|'transaction'|null,
 *   relatedId?: string|null
 * }} input
 */
export async function createTicket(input) {
  const res = await axiosInstance.post('/user/support-tickets', input);
  const body = res.data;
  return { ...body, data: { ...body.data, ticket: normalizeTicket(body.data?.ticket) } };
}

export async function addMessage(id, body) {
  const res = await axiosInstance.post(`/user/support-tickets/${id}/messages`, { body });
  const payload = res.data;
  return { ...payload, data: { ...payload.data, ticket: normalizeTicket(payload.data?.ticket) } };
}

export const supportApi = { listTickets, getTicket, createTicket, addMessage };
export default supportApi;
